import { useRef, useState, type DragEvent, type ChangeEvent, type KeyboardEvent } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import PublishIcon from '@material-ui/icons/Publish';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  uploadArea: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&.dragOver': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  fileInput: {
    display: 'none',
  },
  resultItem: {
    marginBottom: theme.spacing(1),
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  uploadIcon: {
    fontSize: 64,
    color: theme.palette.text.secondary,
  },
  codeBlock: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    color: theme.palette.text.primary,
  },
  infoIcon: {
    color: theme.palette.info.main,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
}));

interface Question {
  title: string;
  content: string;
  tags?: string[];
  entities?: string[];
  answer?: {
    content: string;
    anonymous?: boolean;
  };
  anonymous?: boolean;
}

interface ImportResult {
  success: boolean;
  skipped?: boolean;
  merged?: boolean;
  reason?: string;
  existingQuestionId?: number;
  duplicateOf?: {
    id?: number;
    title?: string;
    reason?: 'title' | 'content';
    source?: 'batch' | 'existing';
  };
  question?: {
    id: number;
    title: string;
  };
  answer?: {
    id: number;
  };
  title?: string;
  error?: string;
}

type DuplicateHandling = 'skip' | 'merge' | 'import';

// Match the backend MAX_BULK_QUESTIONS cap so the UI fails before submit.
export const MAX_BULK_QUESTIONS = 100;

export function assertBulkQuestionCount(questions: Question[]): Question[] {
  if (questions.length > MAX_BULK_QUESTIONS) {
    throw new Error(
      `Maximum ${MAX_BULK_QUESTIONS} questions per import. This file has ${questions.length}. Split it into smaller files.`,
    );
  }
  return questions;
}

// Tokenize CSV text into rows of fields, honoring RFC 4180 quoting: quoted
// fields may contain commas, newlines, and "" escaped quotes.
export const parseCsvRows = (text: string): string[][] => {
  // Excel and other tools prepend a UTF-8 BOM; strip it so the first header
  // is not read as '\ufefftitle'.
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;

  while (i < input.length) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (char === ',') {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (char === '\r' || char === '\n') {
      if (char === '\r' && input[i + 1] === '\n') {
        i += 1;
      }
      row.push(field);
      rows.push(row);
      field = '';
      row = [];
      i += 1;
      continue;
    }
    field += char;
    i += 1;
  }

  // Flush the final field/row (files without a trailing newline).
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
};

export const parseCsv = (text: string): Question[] => {
  // Drop rows that are entirely empty (e.g. blank trailing lines).
  const rows = parseCsvRows(text).filter(cells =>
    cells.some(cell => cell.trim() !== ''),
  );
  if (rows.length < 2) {
    throw new Error('CSV must have header row and at least one data row');
  }

  const headers = rows[0].map(h => h.trim().toLowerCase());
  const titleIdx = headers.indexOf('title');
  const contentIdx = headers.indexOf('content');
  const tagsIdx = headers.indexOf('tags');
  const entitiesIdx = headers.indexOf('entities');
  const answerIdx = headers.indexOf('answer');
  const anonymousIdx = headers.indexOf('anonymous');

  if (titleIdx === -1 || contentIdx === -1) {
    throw new Error('CSV must have "title" and "content" columns');
  }

  return rows.slice(1).map(values => {
    const cell = (idx: number) =>
      idx !== -1 && values[idx] !== undefined ? values[idx].trim() : '';
    // Preserve whitespace for free-form Markdown fields: leading indentation is
    // significant inside fenced code blocks, so trimming would corrupt content.
    const rawCell = (idx: number) =>
      idx !== -1 && values[idx] !== undefined ? values[idx] : '';

    const question: Question = {
      title: cell(titleIdx),
      content: rawCell(contentIdx),
    };

    if (cell(tagsIdx)) {
      question.tags = cell(tagsIdx)
        .split('|')
        .map(t => t.trim())
        .filter(t => t.length > 0);
    }

    if (cell(entitiesIdx)) {
      question.entities = cell(entitiesIdx)
        .split('|')
        .map(e => e.trim())
        .filter(e => e.length > 0);
    }

    if (cell(answerIdx)) {
      question.answer = {
        content: rawCell(answerIdx),
        anonymous: false,
      };
    }

    if (cell(anonymousIdx)) {
      question.anonymous = cell(anonymousIdx).toLowerCase() === 'true';
    }

    return question;
  });
};

export const QetaBulkImportButton = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [duplicateHandling, setDuplicateHandling] =
    useState<DuplicateHandling>('skip');
  const [results, setResults] = useState<{
    imported: number;
    merged?: number;
    skipped?: number;
    failed: number;
    answerFailures?: number;
    processed?: number;
    remaining?: number;
    completed?: boolean;
    total: number;
    results: ImportResult[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const steps = ['Upload File', 'Import', 'Results'];

  const handleOpen = () => {
    setOpen(true);
    setActiveStep(0);
    setFile(null);
    setResults(null);
    setError(null);
    // Clear the native input so re-selecting the same file still fires `change`.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFile(null);
    setResults(null);
    setError(null);
    setActiveStep(0);
    // Clear the native input so reopening and picking the same file re-triggers
    // `change` (the browser suppresses it when the value is unchanged).
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const parseJson = (text: string): Question[] => {
    const data = JSON.parse(text);
    
    if (Array.isArray(data)) {
      return data;
    }
    
    if (data.questions && Array.isArray(data.questions)) {
      return data.questions;
    }
    
    throw new Error('JSON must be an array or object with "questions" array');
  };

  const parseFile = async (uploadedFile: File): Promise<Question[]> => {
    const text = await uploadedFile.text();
    const extension = uploadedFile.name.split('.').pop()?.toLowerCase();

    if (extension === 'csv') {
      return assertBulkQuestionCount(parseCsv(text));
    } else if (extension === 'json') {
      return assertBulkQuestionCount(parseJson(text));
    }
    throw new Error('Unsupported file type. Please upload CSV or JSON file.');
  };

  const handleFileSelect = async (selectedFile: File) => {
    try {
      setError(null);
      setFile(selectedFile);

      // Validate file can be parsed, but stay on the upload step so the
      // "Start Import" button remains visible for the user to trigger import.
      await parseFile(selectedFile);
    } catch (err: any) {
      setError(err.message);
      setFile(null);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  // Keyboard-only users can't reach the visually-hidden input, so open the
  // native file dialog when the focusable upload area is activated.
  const handleUploadKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      setImporting(true);
      setError(null);
      setActiveStep(1);

      const questions = assertBulkQuestionCount(await parseFile(file));
      
      // Get auth token
      const { token } = await identityApi.getCredentials();
      const backendUrl = config.getString('backend.baseUrl');

      // Only send a bearer header when a token is present; cookie-only auth has
      // none, and 'Bearer undefined' would be rejected. credentials: 'include'
      // forwards the auth cookie in that case.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Call bulk import API
      // Duplicates are handled server-side via `duplicateHandling` during the
      // import; the UI intentionally does not call the API-only /check-duplicates.
      const response = await fetch(`${backendUrl}/api/qeta/bulk-import/questions`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ 
          questions,
          duplicateHandling,
        }),
      });

      if (!response.ok) {
        // Surface the backend's error body (e.g. a 403 reason) rather than the
        // generic status text, falling back when the body is missing/unparsable.
        const detail = await response
          .json()
          .then(body => body?.error?.message || body?.message)
          .catch(() => undefined);
        throw new Error(
          `Import failed: ${detail || response.statusText || response.status}`,
        );
      }

      const result = await response.json();
      setResults(result);
      setActiveStep(2);
    } catch (err: any) {
      setError(err.message);
      // Return to the upload step so the user can retry; the progress view has
      // no retry control of its own.
      setActiveStep(0);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<PublishIcon />}
        onClick={handleOpen}
        style={{ flexShrink: 0 }}
      >
        Bulk Import
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Import Questions & Answers</DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box mt={3}>
            {error && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {error}
              </Alert>
            )}

            {/* Step 1: Upload File */}
            {activeStep === 0 && (
              <Box>
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={handleFileInputChange}
                  className={classes.fileInput}
                  id="bulk-import-file"
                  ref={fileInputRef}
                />
                <label htmlFor="bulk-import-file">
                  <Box
                    className={`${classes.uploadArea} ${dragOver ? 'dragOver' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onKeyDown={handleUploadKeyDown}
                    role="button"
                    tabIndex={0}
                    aria-label="Upload CSV or JSON file. Press Enter or Space to browse, or drag and drop a file here."
                  >
                    <CloudUploadIcon className={classes.uploadIcon} />
                    <Typography variant="h6" gutterBottom>
                      Drag & Drop or Click to Upload
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Supported formats: CSV, JSON
                    </Typography>
                    {file && (
                      <Typography variant="body1" style={{ marginTop: 16 }}>
                        Selected: <strong>{file.name}</strong>
                      </Typography>
                    )}
                  </Box>
                </label>

                <Box mt={3}>
                  <FormControl fullWidth>
                    <InputLabel id="duplicate-handling-label">
                      Duplicate handling
                    </InputLabel>
                    <Select
                      labelId="duplicate-handling-label"
                      value={duplicateHandling}
                      onChange={e =>
                        setDuplicateHandling(
                          e.target.value as DuplicateHandling,
                        )
                      }
                    >
                      <MenuItem value="skip">
                        Skip duplicates (recommended)
                      </MenuItem>
                      <MenuItem value="merge">
                        Merge answer into existing question
                      </MenuItem>
                      <MenuItem value="import">
                        Import everything (allow duplicates)
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    style={{ marginTop: 4 }}
                  >
                    Duplicates are matched by title or highly similar content,
                    against existing questions and within this file.
                  </Typography>
                </Box>

                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    CSV Format Example:
                  </Typography>
                  <Typography variant="body2" component="pre" className={classes.codeBlock}>
{`title,content,tags,entities,answer
How to deploy?,Steps for deployment,deployment|devops,component:default/my-service,Run tests then merge
API documentation?,Where are API docs?,docs|api,,Check docs portal`}
                  </Typography>
                  
                  <Typography variant="caption" display="block" gutterBottom>
                    • Use pipe (|) to separate multiple tags or entities
                    <br />
                    • Answer column is optional
                    <br />
                    • Maximum {MAX_BULK_QUESTIONS} questions per file
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Step 2: Import progress */}
            {activeStep === 1 && (
              <Box py={4}>
                <LinearProgress />
                <Typography align="center" style={{ marginTop: 16 }}>
                  Importing questions...
                </Typography>
              </Box>
            )}

            {/* Step 3: Results */}
            {activeStep === 2 && results && (
              <Box>
                <Alert
                  severity={
                    results.completed === false
                      ? 'warning'
                      : results.failed === 0 &&
                        (results.skipped || 0) === 0 &&
                        (results.answerFailures || 0) === 0
                      ? 'success'
                      : 'warning'
                  }
                  style={{ marginBottom: 16 }}
                >
                  <strong>
                    {results.completed === false
                      ? 'Import Incomplete:'
                      : 'Import Complete:'}
                  </strong>{' '}
                  {results.imported} imported
                  {results.merged ? `, ${results.merged} merged` : ''}
                  {results.skipped ? `, ${results.skipped} skipped` : ''}
                  {results.failed > 0 ? `, ${results.failed} failed` : ''}
                  {results.answerFailures
                    ? `, ${results.answerFailures} answer failures`
                    : ''}{' '}
                  out of {results.total} total
                  {results.completed === false && (results.remaining ?? 0) > 0 ? (
                    <>
                      {'. '}
                      {results.remaining} question(s) were not processed before the
                      time limit.{' '}
                      {duplicateHandling === 'import'
                        ? 'Re-run only the unprocessed rows; import mode does not deduplicate, so re-sending imported rows would create duplicates.'
                        : duplicateHandling === 'merge'
                          ? 'Re-run only the unprocessed rows; re-sending already-merged rows can attach duplicate answers.'
                          : 'Re-run the import; already-imported questions are detected and skipped automatically.'}
                    </>
                  ) : (
                    ''
                  )}
                </Alert>

                <List>
                  {results.results.map((result, index) => (
                    <ListItem key={index} className={classes.resultItem}>
                      <ListItemIcon>
                        {result.success ? (
                          result.merged ? (
                            <InfoIcon className={classes.infoIcon} />
                          ) : result.skipped ? (
                              <InfoIcon className={classes.warningIcon} />
                          ) : (
                            <CheckCircleIcon className={classes.successIcon} />
                              )
                        ) : (
                          <ErrorIcon className={classes.errorIcon} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={result.question?.title || result.title}
                        secondary={
                          result.success
                            ? result.merged
                              ? `Answer merged into existing question ID: ${result.duplicateOf?.id}`
                              : result.skipped
                                ? `Skipped duplicate (${result.duplicateOf?.reason} match${result.duplicateOf?.id ? `, existing ID: ${result.duplicateOf.id}` : ''})`
                                : `Question ID: ${result.question?.id}${result.answer ? `, Answer ID: ${result.answer.id}` : ''}`
                            : `Error: ${result.error}`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            {results ? 'Close' : 'Cancel'}
          </Button>
          {activeStep === 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleImport}
              disabled={!file || importing}
            >
              Start Import
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
